import {
  GetQConfigByIdDocument,
  type UpdateQuestionQConfigStructureLocationTypeInput,
  useGetItemSetQuestionPropertiesCountByLocationQuery,
  useUpdateItemSetQuestionQConfigStructureMutation,
} from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { useParams } from '@exam-management/router';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
  Text,
} from '@shared/components';
import { useHasPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type Path } from 'react-hook-form';

import {
  ChapterInfoSection,
  ItemSetApplyConditionErrorAlert,
  ItemSetQuestionConditionsTable,
  QConfigItemSetQuestionConditionsFormFields,
  QuestionSummarySection,
  type TableData,
} from '../../components';
import { useFetchLos, useGetSelectedLOSForItemSet } from '../../hooks';
import {
  type UpdateItemSetQuestionConditionsFormInput,
  useUpdateItemSetQuestionConditions,
  useUpdateItemSetQuestionConditionsFormContext,
} from '../../providers';
import {
  calculateSummaryData,
  CONTENT_TYPE_TEXT_MAP,
  formatConditions,
  formatTableData,
  formatTopicsOrSubtopics,
  getLocationTypeByContentType,
  setErrorsByInvalidConditions,
  validateItemSetConditions,
  validateLOSSelectionAndSetError,
} from '../../utils';
import { QConfigItemSetQuestionPropertiesFormFields } from '../item-set-question-properties-form-fields';

function getNoOptionsText(hasViewPermission: boolean) {
  if (!hasViewPermission) {
    return 'ไม่มีสิทธิ์การเข้าถึง กรุณาติดต่อผู้ดูแลระบบ';
  }

  return 'ไม่พบข้อมูล';
}

interface UpdateItemSetQuestionsConditionsFormLogicProps {
  onClickSelectTopic: () => void;
  onClose: () => void;
  onCancel: () => void;
}

export default function UpdateItemSetQuestionsConditionsFormLogic(
  props: Readonly<UpdateItemSetQuestionsConditionsFormLogicProps>,
) {
  const { onClickSelectTopic, onClose, onCancel } = props;
  const [appliedConditions, setAppliedConditions] = useState<boolean>(false);
  const [showAppliedConditionsError, setShowAppliedConditionsError] = useState<boolean>(false);
  const [questionConditionsData, setQuestionConditionsData] = useState<
    Array<{
      data: TableData;
      total?: number;
    }>
  >([]);
  const [summaryData, setSummaryData] = useState<{
    totalItemSetQuestions: string;
    totalScore: number;
    selectedQuestions: number;
  } | null>(null);
  const { qconfigId: qConfigId } = useParams('/exam-courses/:courseId/qconfigs/:qconfigId/details');

  const { showSnackbar } = useSnackbar();
  const {
    questionTypeStructureId,
    chapterData,
    levelItemsWithLevel,
    structures: qConfigStructures,
    defaultLOS,
  } = useUpdateItemSetQuestionConditions();
  const formContext = useUpdateItemSetQuestionConditionsFormContext();
  const { watch, getValues, setError, clearErrors } = formContext;
  const { location, los, contentType, itemSetType: questionSize } = watch();

  const existingLOS = useGetSelectedLOSForItemSet({
    chapterId: chapterData.id,
    qConfigStructures,
    defaultLOS,
  });
  const { options, isSearchLOSLoading } = useFetchLos({
    chapterLevelItemId: chapterData.refId,
    contentType,
  });

  const selectedLOSIds = useMemo(() => {
    if (!los) return [];
    return los.map((item) => item.value);
  }, [los]);

  const selectedSubtopics = useMemo(() => {
    const subtopics = location.subtopics;
    return formatTopicsOrSubtopics(subtopics);
  }, [location]);

  const {
    data: getItemSetQuestionPropertiesCountResult,
    loading: isGetItemSetQuestionPropertiesCountLoading,
    refetch: getItemSetQuestionPropertiesCountByLocation,
  } = useGetItemSetQuestionPropertiesCountByLocationQuery({
    fetchPolicy: 'network-only',
    variables: {
      input: {
        qBankStructureLevelItemIds: contentType === 'TOPIC' ? selectedSubtopics : undefined,
        losIds: contentType === 'LOS' ? selectedLOSIds : undefined,
        type: getLocationTypeByContentType(contentType),
        size: questionSize ? parseInt(questionSize as unknown as string) : 0,
      },
    },
  });

  const selectedContent = useMemo(() => {
    if (contentType === 'TOPIC') {
      return selectedSubtopics;
    }

    if (contentType === 'LOS') {
      return selectedLOSIds;
    }

    return [];
  }, [contentType, selectedSubtopics, selectedLOSIds]);

  const hasViewPermission = useHasPermission(APP_PERMISSIONS.QBANK.LOS.VIEW);
  const isLocationSelected = selectedContent.length > 0;

  const contentTypeText = useMemo(() => CONTENT_TYPE_TEXT_MAP[contentType], [contentType]);

  const handleApplyConditions = useCallback(
    async (data: UpdateItemSetQuestionConditionsFormInput) => {
      setShowAppliedConditionsError(false);

      const isValid = await formContext.trigger();
      if (!isValid) return;

      const itemSetSize = parseInt(data.itemSetType as unknown as string);

      const formattedLocation =
        contentType === 'TOPIC'
          ? formatTopicsOrSubtopics(data.location.subtopics)
          : (data.los?.map((item) => item.value) ?? []);

      const { data: getPropertiesCountResult } = await getItemSetQuestionPropertiesCountByLocation({
        input: {
          type: getLocationTypeByContentType(contentType),
          size: itemSetSize,
          losIds: contentType === 'LOS' ? formattedLocation : undefined,
          qBankStructureLevelItemIds: contentType === 'TOPIC' ? formattedLocation : undefined,
        },
      });
      setAppliedConditions(true);

      const itemSetConditionsValidation = validateItemSetConditions({
        conditions: formContext.getValues('conditions'),
        queryResult: getPropertiesCountResult?.qctGetItemSetQuestionPropertiesCountByLocation,
      });

      if (!itemSetConditionsValidation.isValid) {
        setErrorsByInvalidConditions(
          itemSetConditionsValidation.results.invalidConditions,
          setError,
        );
        setQuestionConditionsData(
          formatTableData(itemSetConditionsValidation.results.validConditions),
        );
      } else {
        const scorePerQuestion = data.scorePerQuestion ?? 0;
        setQuestionConditionsData(
          formatTableData(itemSetConditionsValidation.results.validConditions),
        );
        setSummaryData(
          calculateSummaryData({
            itemSetType: itemSetSize,
            allQueriedQuestions: itemSetConditionsValidation.results.allQueriedQuestions,
            totalValidQuestions: itemSetConditionsValidation.results.totalValidQuestions,
            scorePerQuestion,
          }),
        );
      }
    },
    [contentType, formContext, getItemSetQuestionPropertiesCountByLocation],
  );

  const [
    updateItemSetQuestionQConfigStructure,
    { loading: isUpdatingItemSetQuestionQConfigStructure },
  ] = useUpdateItemSetQuestionQConfigStructureMutation({
    onCompleted: () => {
      showSnackbar({
        message: `${contentTypeText} has been saved`,
        variant: 'success',
      });
      onClose();
    },
    onError: () => {
      showSnackbar({
        message: `Unable to Save ${contentTypeText}`,
        variant: 'error',
      });
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetQConfigByIdDocument,
        variables: {
          getQConfigByIdInput: {
            qConfigId,
          },
        },
      },
    ],
  });

  const handleSave = useCallback(
    async (data: UpdateItemSetQuestionConditionsFormInput) => {
      if (!appliedConditions) {
        setShowAppliedConditionsError(true);
        throw new Error('Validate failed: Please apply conditions before saving.');
      }

      const itemSetConditionsValidation = validateItemSetConditions({
        conditions: data.conditions,
        queryResult:
          getItemSetQuestionPropertiesCountResult?.qctGetItemSetQuestionPropertiesCountByLocation,
      });

      if (!itemSetConditionsValidation.isValid) {
        setErrorsByInvalidConditions(
          itemSetConditionsValidation.results.invalidConditions,
          setError,
        );
        throw new Error('Validate failed: Conditions are not valid.');
      }

      if (!validateLOSSelectionAndSetError(data.los, existingLOS, setError)) {
        throw new Error('Validate failed: LOS selection is not valid.');
      }

      const conditions = formatConditions(data.conditions);

      const formattedLocation =
        contentType === 'TOPIC'
          ? formatTopicsOrSubtopics(data.location.subtopics)
          : (data.los?.map((item) => item.value) ?? []);

      await updateItemSetQuestionQConfigStructure({
        variables: {
          input: {
            id: questionTypeStructureId,
            conditions,
            scorePerQuestion: data.scorePerQuestion,
            size: parseInt(data.itemSetType as unknown as string),
            location: {
              locationType: getLocationTypeByContentType(
                data.contentType,
              ) as unknown as UpdateQuestionQConfigStructureLocationTypeInput,
              losIds: contentType === 'LOS' ? formattedLocation : undefined,
              qBankStructureLevelItemIds: contentType === 'TOPIC' ? formattedLocation : undefined,
            },
          },
        },
      });
    },
    [
      appliedConditions,
      contentType,
      getItemSetQuestionPropertiesCountResult,
      setError,
      existingLOS,
      questionTypeStructureId,
      updateItemSetQuestionQConfigStructure,
    ],
  );

  useEffect(() => {
    // REMARK: this useEffect for handle initial summary data and question conditions data when edit modal first init
    if (!questionConditionsData.length && !summaryData && getItemSetQuestionPropertiesCountResult) {
      const itemSetConditionsValidation = validateItemSetConditions({
        conditions: getValues('conditions'),
        queryResult:
          getItemSetQuestionPropertiesCountResult?.qctGetItemSetQuestionPropertiesCountByLocation,
      });

      setQuestionConditionsData(
        formatTableData(itemSetConditionsValidation.results.validConditions),
      );
      setSummaryData(
        calculateSummaryData({
          allQueriedQuestions: itemSetConditionsValidation.results.allQueriedQuestions,
          totalValidQuestions: itemSetConditionsValidation.results.totalValidQuestions,
          itemSetType: parseInt(getValues('itemSetType') as unknown as string),
          scorePerQuestion: getValues('scorePerQuestion') ?? 0,
        }),
      );
    }
  }, [getItemSetQuestionPropertiesCountResult]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name?.includes('conditions') || name === 'los' || name?.includes('location')) {
        setAppliedConditions(false);
        setShowAppliedConditionsError(false);

        const splittedConditionName = name.split('.');
        if (splittedConditionName.length > 1) {
          const targetPath = splittedConditionName.slice(0, 2).join('.');
          clearErrors(targetPath as Path<UpdateItemSetQuestionConditionsFormInput>);
        }
      }

      if (name === 'scorePerQuestion') {
        if (value.itemSetType && value.scorePerQuestion) {
          const itemSetConditionsValidation = validateItemSetConditions({
            conditions: formContext.getValues('conditions'),
            queryResult:
              getItemSetQuestionPropertiesCountResult?.qctGetItemSetQuestionPropertiesCountByLocation,
          });

          if (itemSetConditionsValidation.isValid) {
            setSummaryData(
              calculateSummaryData({
                itemSetType: parseInt(value.itemSetType as unknown as string),
                allQueriedQuestions: itemSetConditionsValidation.results.allQueriedQuestions,
                totalValidQuestions: itemSetConditionsValidation.results.totalValidQuestions,
                scorePerQuestion: value.scorePerQuestion ?? 0,
              }),
            );
          }
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, getItemSetQuestionPropertiesCountResult, setSummaryData, formContext, clearErrors]);

  const isSubmitting = useMemo(() => {
    const formSubmitting = formContext.formState.isSubmitting;

    return formSubmitting || isUpdatingItemSetQuestionQConfigStructure;
  }, [formContext.formState.isSubmitting, isUpdatingItemSetQuestionQConfigStructure]);

  const isSubmitDisabled = useMemo(() => {
    const formDirty = formContext.formState.isDirty;

    return !formDirty || !isLocationSelected || !questionSize;
  }, [formContext.formState.isDirty, isLocationSelected, questionSize]);

  return (
    <>
      <DialogTitle onClose={onClose} loading={isSubmitting}>
        Edit {contentTypeText}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ height: '600px' }}>
        <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
          <ChapterInfoSection
            title={chapterData?.name ?? 'บท: ไม่ระบุ'}
            qSource={chapterData?.refCode ?? '-'}
            questionType='item-set'
          />
          <QConfigItemSetQuestionConditionsFormFields
            hideContentSelector={false}
            slotProps={{
              contentSelectorByContentType: {
                onEdit: onClickSelectTopic,
                location,
                contentType,
                levelItemsWithLevel,
                questionSize,
                group: {
                  options,
                  loading: isSearchLOSLoading,
                  noOptionsText: getNoOptionsText(hasViewPermission),
                  hasViewPermission,
                  existingLOS,
                },
              },
              itemSetTypeSelection: {
                itemSetType: questionSize,
                isEditModal: true,
              },
              contentTypeSection: {
                contentType,
                isEditModal: true,
              },
            }}
          />
          {isLocationSelected ? (
            <>
              <QConfigItemSetQuestionPropertiesFormFields
                itemSetType={questionSize}
                onApplyConditions={() => handleApplyConditions(getValues())}
                isLoading={isGetItemSetQuestionPropertiesCountLoading}
                formContext={formContext}
              />
              <Text variant='body1Semibold'>จำนวนข้อสอบที่มีในคลัง (ตามระดับความยาก)</Text>
              <QuestionSummarySection
                totalItemSetQuestions={summaryData?.totalItemSetQuestions}
                totalScore={summaryData?.totalScore}
                selectedQuestions={summaryData?.selectedQuestions}
              />
              {showAppliedConditionsError ? <ItemSetApplyConditionErrorAlert /> : null}
              <ItemSetQuestionConditionsTable
                questionConditionsData={questionConditionsData}
                questionSize={questionSize}
              />
            </>
          ) : null}
        </Flex>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button size='large' type='button' color='secondary' variant='outlined' onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size='large'
          type='button'
          onClick={formContext.handleSubmit(handleSave)}
          loading={isSubmitting}
          disabled={isSubmitDisabled}
        >
          Save
        </Button>
      </DialogActions>
    </>
  );
}
